import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { loggedUserTypes, HandleUserType } from "../types/auth.types";
import { defaultUserData } from "../constants/userData";
import { verify } from "../services/authService";
import { log } from "../utils/logger";

interface UserContextType {
    loggedIn: boolean;
    logout: () => void;
    userData: loggedUserTypes;
    handleUserData: HandleUserType;
    setUserData: (val: loggedUserTypes) => void;
    refreshUser: () => Promise<void>;
    syncTokenFromCookie: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const TOKEN_KEY = "CatermatAuthToken";
const USERDATA_KEY = "userData";

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
    const [token, setToken] = useState<string | undefined>(() => Cookies.get(TOKEN_KEY));
    const [userData, setUserData] = useState<loggedUserTypes>(defaultUserData);

    const loggedIn = !!token;
    // Subscriber/profile cache must stay disabled to avoid stale cart identity.
    useEffect(() => {
        sessionStorage.removeItem(USERDATA_KEY);
    }, []);

    const clearUser = () => {
        setUserData(defaultUserData);
        sessionStorage.removeItem(USERDATA_KEY);
    };

    const refreshUser = async () => {
        if (!token) return;
        try {
            const data = await verify();
            //log(data)
            setUserData(data);
        } catch (e) {
            // token invalid/expired -> hard reset
            Cookies.remove(TOKEN_KEY);
            setToken(undefined);
            clearUser();
        }
    };

    const syncTokenFromCookie = () => {
        setToken(Cookies.get(TOKEN_KEY));
    };

    // Verify on mount and whenever token changes
    useEffect(() => {
        if (!token) {
            clearUser();
            return;
        }
        refreshUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleUserData: HandleUserType = (key, value) => {
        setUserData((prev) => ({ ...prev, [key]: value }));
    };

    const logout = () => {
        Cookies.remove(TOKEN_KEY);
        setToken(undefined);
        clearUser();
    };

    const value = useMemo(
        () => ({
        loggedIn,
        logout,
        userData,
        handleUserData,
        setUserData,
        refreshUser,
        syncTokenFromCookie,
        }),
        [loggedIn, userData]
    );

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => {
    const context = useContext(UserContext);
    if (!context) throw new Error("useUserContext must be used within a UserProvider");
    return context;
};
